import { useRecoilValue } from 'recoil';
import { atom_routeToStopLocation, atom_isNavigationMode } from '@app/shared/state/tour.atom';
import {
  atom_guideRoute,
  selector_isTrackerActive,
  selector_trackerRouteType,
  selector_trackerTargetStopId,
} from '@app/shared/state/route-tracker.atom';

/**
 * Centralized control over which route polylines animate.
 *
 * Rules:
 * 1. When routeToStop is active, guide route becomes static.
 * 2. When navigator is active in guide-route mode, only the segment
 *    leading to the current target stop animates; all others are static.
 *
 * Uses per-field selectors so this hook doesn't re-render on GPS-tick churn
 * (distance, closestStopIndex, etc.) — only on the fields it actually reads.
 */
export function usePolylineAnimation() {
  const routeToStop = useRecoilValue(atom_routeToStopLocation);
  const navigatorEnabled = useRecoilValue(atom_isNavigationMode);
  const isTrackerActive = useRecoilValue(selector_isTrackerActive);
  const routeType = useRecoilValue(selector_trackerRouteType);
  const targetStopId = useRecoilValue(selector_trackerTargetStopId);
  const guideRoute = useRecoilValue(atom_guideRoute);

  const hasRouteToStop = routeToStop !== null;

  // Determine the active guide segment (fromStopId → toStopId) when navigating
  let activeGuideSegment: { fromStopId: string; toStopId: string } | null = null;

  if (navigatorEnabled && isTrackerActive && routeType === 'guide-route' && targetStopId && guideRoute?.segments) {
    // Same matching logic as GuideStepMiniPanel: prefer segment ending at target,
    // fall back to segment starting from target, then forward-walk for "none"-type stops.
    let seg = guideRoute.segments.find(s => s.toStopId === targetStopId);
    if (!seg) seg = guideRoute.segments.find(s => s.fromStopId === targetStopId);
    if (!seg) {
      const idx = guideRoute.stopOrder.indexOf(targetStopId);
      if (idx >= 0) {
        for (let i = idx + 1; i < guideRoute.stopOrder.length; i++) {
          const id = guideRoute.stopOrder[i];
          seg = guideRoute.segments.find(s => s.toStopId === id || s.fromStopId === id);
          if (seg) break;
        }
      }
    }
    if (seg) {
      activeGuideSegment = { fromStopId: seg.fromStopId, toStopId: seg.toStopId };
    }
  }

  // Guide route animation: off when routeToStop is active, or per-segment when navigating
  const guideRouteAnimated = !hasRouteToStop && !activeGuideSegment;

  return {
    guideRouteAnimated,
    routeToStopAnimated: true,
    activeGuideSegment,
  };
}
